import { Center, Spinner, Text } from '@chakra-ui/react';
import { ChatMessageAuthorType } from 'enums/modules/chat/chatMessageAuthorType';
import { ChatRoomType } from 'enums/modules/chat/chatRoomType';
import reverse from 'lodash/reverse';
import { ChatMessage } from 'model/modules/chat/chatMessage';
import { FC, MutableRefObject } from 'react';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroller';
import { useMount } from 'react-use';
import { formatDateSeparator, isSameDay } from 'utils/formatMessageTime';
import ChatMessageRow from './message/ChatMessageRow';
import ChatDateSeparator from './message/ChatDateSeparator';

interface Props extends PaginationListProps<ChatMessage> {
  scrollEndRef: MutableRefObject<HTMLDivElement | null>;
  roomType: ChatRoomType;
}

const MessageListLoaded: FC<Props> = ({
  items,
  isFetching,
  setPage,
  currentPage,
  pageCount,
  scrollEndRef,
  roomType,
}) => {
  const { t } = useTranslation();

  useMount(() => {
    queueMicrotask(() => {
      scrollEndRef.current?.scrollIntoView();
    });
  });

  const reversedItems = reverse([...items]);

  if (items.length > 0) {
    return (
      <InfiniteScroll
        loadMore={() => {
          if (!isFetching && currentPage < pageCount) {
            setPage(currentPage + 1);
          }
        }}
        hasMore={currentPage < pageCount}
        loader={
          <Center p={5} key="loading">
            <Spinner />
          </Center>
        }
        useWindow={false}
        isReverse
        initialLoad={false}
        style={{
          minHeight: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          width: '100%',
        }}
      >
        {reversedItems.map((item, index) => {
          const lastIndex = reversedItems.length - 1;
          const isPreviousMessageFromDifferentUser =
            index === 0 ||
            (reversedItems[index - 1].authorType !== item.authorType || reversedItems[index - 1].managerName !== item.managerName || reversedItems[index - 1].authorData?.authorId !== item.authorData?.authorId);
          const isNextMessageFromDifferentUser =
            index === lastIndex ||
            (reversedItems[index + 1].authorType !== item.authorType || reversedItems[index + 1].managerName !== item.managerName || reversedItems[index + 1].authorData?.authorId !== item.authorData?.authorId);

          const shouldShowDateSeparator = index === 0 ||
            !isSameDay(new Date(item.createdAt), new Date(reversedItems[index - 1].createdAt));

          const elements = [];

          if (shouldShowDateSeparator) {
            const dateString = formatDateSeparator(new Date(item.createdAt), t);
            elements.push(
              <ChatDateSeparator
                key={`date-separator-${item.id ?? item.sendingId}`}
                date={dateString}
              />
            );
          }

          elements.push(
            <ChatMessageRow
              authorName={item.authorData?.fullname ?? item.managerName ?? ''}
              roomType={roomType}
              avatar={item.authorData?.photo?.url ?? null}
              isPreviousMessageFromDifferentUser={
                isPreviousMessageFromDifferentUser
              }
              isNextMessageFromDifferentUser={isNextMessageFromDifferentUser}
              isSelfMessage={item.authorData?.isMe ?? item.authorType === ChatMessageAuthorType.Investor}
              key={item.id ?? item.sendingId}
              {...item}
            />
          );

          return elements;
        })}
      </InfiniteScroll>
    );
  } else {
    return (
      <Center h="100%">
        <Text textAlign="center">{t('listIsEmpty')}</Text>
      </Center>
    );
  }
};

export default MessageListLoaded;
